import moment from '@this/lib/moment';
import { digits, isAiTravel } from '@this/src/util';
import type OrderItem from '@this/domain/order_item2';
import type Trip from '@this/domain/trip/trip2';
import TransportElement from '@this/domain/transport_element2';
import type { Element } from '@this/domain/order_item2';
import HotelElement, { HOTEL_PLAN_POINT_NOTICE } from '@this/domain/hotel_element2';
import { buildShinkansenTicketPickupNotice } from '@this/domain/shinkansen_ticket_pickup_notice';
import { AIR_TICKET_TYPE_RAKUTEN_PACKAGE } from '@this/domain/transport_element/transport_element';

export interface Translatedline {
  label: string;
  value: string;
  translate: boolean;
  kind?: string;
}

export interface Theme {
  themeClass: string;
  serviceName: string;
}

const addBold = (text: string) => `<b>${text}</b>`;
const addLink = (url: string, text?: string) => `<a href="${url}">${text || url}</a>`;

const isQrTicketingMethod = (ticketingMethod?: string): boolean =>
  ticketingMethod === 'qr_east' || ticketingMethod === 'qr_west';

const pushQrNoticeLines = (result: string[], ticketingMethod?: string, pickupStation?: string | null) => {
  const notice = buildShinkansenTicketPickupNotice(ticketingMethod, pickupStation);
  if (!notice) return;

  result.push('');
  result.push(notice.title);
  notice.descriptionLines.forEach(line => result.push(line));
  if (notice.phoneNumber) {
    result.push(`「${notice.phoneNumber}」をご入力ください。`);
    result.push('');
  }
  result.push(notice.noteLine);
  notice.bulletLines.forEach(line => result.push(line));
};

const formatRoomTypeDetailForCalendar = (
  detail: Translatedline,
  calendarType: 'google' | 'outlook'
): Translatedline => {
  const isBedTypeNotice =
    detail.value.includes('ベッドタイプ：') &&
    detail.value.includes('※本プランでは予約確定後でもベッドタイプは確約されません。');

  if (calendarType === 'outlook') {
    if (isBedTypeNotice) {
      // Outlookでベッドタイプ注意書きの場合：\nを<br>　に変換してインデント調整
      const valueWithBr = detail.value.replace(/\n/g, '<br>　');
      return {
        label: `　${detail.label}`,
        value: valueWithBr,
        translate: detail.translate
      };
    }
    return {
      label: `　${detail.label}`,
      value: detail.value,
      translate: detail.translate
    };
  }

  return {
    label: `　${detail.label}`,
    value: detail.value,
    translate: detail.translate
  };
};

export const createCalendarLink = (
  title: string,
  details: string,
  start: string,
  end: string,
  calendarType: 'google' | 'outlook',
  allday = false
): string => {
  const baseUrls: { [key: string]: string } = {
    google: 'https://www.google.com/calendar/render',
    outlook: 'https://outlook.live.com/calendar/deeplink/compose'
  };

  const params = new URLSearchParams();

  if (calendarType === 'google') {
    params.set('action', 'TEMPLATE');
    params.set('text', title);
    params.set('details', details);
    params.set('dates', `${start}/${end}`);
  }

  if (calendarType === 'outlook') {
    params.set('path', '/calendar/action/compose');
    params.set('rru', 'addevent');
    params.set('subject', title);
    params.set('body', details);
    params.set('startdt', start);
    params.set('enddt', end);
    params.set('allday', allday ? 'true' : 'false');
  }

  return `${baseUrls[calendarType]}?${params.toString()}`;
};

export const isAllday = (orderItem: OrderItem): boolean => {
  return orderItem.orderItemCategory === 'hotel';
};

export const formatDate = (
  orderItem: OrderItem,
  calendarType: 'google' | 'outlook',
  isEndDate = false
): string => {
  const date = isEndDate ? orderItem.endTime : orderItem.startTime;
  const isAllDay = isAllday(orderItem);

  // 交通機関の場合、実際の出発/到着時刻を使用する
  let transportElement;
  if (orderItem.elements && orderItem.elements.length > 0) {
    const isTransport = (element: Element): element is TransportElement => element instanceof TransportElement;

    if (isEndDate) {
      // 終了日時の場合は、最後の交通機関要素を探す
      const transportElements = orderItem.elements.filter(isTransport);
      transportElement = transportElements.length > 0 ? transportElements[transportElements.length - 1] : null;
    } else {
      // 開始日時の場合は、最初の交通機関要素を探す
      transportElement = orderItem.elements.find(isTransport);
    }
  }

  if (transportElement) {
    const timeObj = isEndDate ? transportElement?.to?.time : transportElement?.from?.time;

    if (timeObj) {
      return calendarType === 'outlook'
        ? moment(timeObj).format('YYYY-MM-DDTHH:mm:ss')
        : moment(timeObj).format('YYYYMMDDTHHmmSS');
    }
  }

  const formatString =
    calendarType === 'google'
      ? isAllDay
        ? 'YYYYMMDD'
        : 'YYYYMMDDTHHmmSSZ'
      : isAllDay
      ? 'YYYY-MM-DD'
      : 'YYYY-MM-DDTHH:mm:ssZ';

  let formattedDate = moment(date).format(formatString);

  // endDateに1日を追加(カレンダーの仕様で終日にすると1日前の日時になってしまうため)
  if (isEndDate && isAllDay) {
    formattedDate = moment(formattedDate).add(1, 'days').format(formatString);
  }

  return formattedDate;
};

export const formatMainContentBodyText = (
  orderItem: OrderItem,
  trip: Trip,
  userId: number,
  theme: Theme,
  serviceId: number,
  calendarType: 'google' | 'outlook' = 'google'
) => {
  const result: string[] = [];
  const formatDetails = (details: Translatedline[]) =>
    details.forEach(({ label, value }) => result.push(`${label ? `${label} ` : ''}${value}`));

  const formatTravelerTypes = (travelerTypes: string[]) =>
    travelerTypes?.forEach((travelerType, i) =>
      result.push(`${travelerTypes.length > 1 ? `${i + 1}人目` : '出張者'}: ${travelerType}`)
    );

  orderItem.elements.forEach(element => {
    if (element instanceof HotelElement) {
      // Hotel Info
      result.push(addBold('旅程情報'));
      result.push(element.name);
      result.push('');

      if (element.hotelReservationNumber) {
        result.push(`予約番号：${element.hotelReservationNumber}`);
      }

      const departureArrivalDetails = [
        {
          label: '[チェックイン]　',
          value: `${element.checkinDate.format('YYYY-MM-DD')} ${element.checkinTime || ''}`,
          translate: false
        },
        {
          label: '[チェックアウト]',
          value: `${element.checkoutDate.format('YYYY-MM-DD')} ${element.checkoutTime || ''}`,
          translate: false
        },
        { label: '', value: '※ 表記は現地時刻です。', translate: true }
      ];
      formatDetails(departureArrivalDetails);
      result.push('');

      result.push(
        `[返金] ${
          element.refundable === true ? '可' : element.refundable === false ? '不可' : 'お問い合わせください'
        }`
      );

      const hotelInfoDetails = [{ label: '[プラン]', value: '', translate: false }]
        .concat(
          element.structuredHotelTypeDetail().flatMap((detail: Translatedline) => {
            const detailLine = {
              label: `　${detail.label}`,
              value: detail.value.replace(/^\n+/, '').trim(),
              translate: detail.translate,
              kind: detail.kind
            };
            if (detail.kind === 'plan_name') {
              return [detailLine, { label: '', value: HOTEL_PLAN_POINT_NOTICE, translate: true }];
            }
            return [detailLine];
          })
        )
        .concat([
          { label: '　住所', value: element.address || '', translate: false },
          { label: '　電話番号', value: element.tel || '', translate: false }
        ])
        .concat(
          element
            .structuredRoomTypeDetail()
            .map((detail: Translatedline) => formatRoomTypeDetailForCalendar(detail, calendarType))
        );
      formatDetails(hotelInfoDetails);

      if (element.cancelPolicy) {
        result.push('');
        result.push('[キャンセルポリシー]');

        if (element.cancelPolicy && typeof element.cancelPolicy === 'string') {
          const cancelPolicyLines = element.cancelPolicy.split('\n').filter((line: string) => line.trim());
          cancelPolicyLines.forEach((line: string) => result.push(line.trim()));
        }
      }
    } else if (element instanceof TransportElement) {
      // Transport Info
      result.push(`旅程番号: ${trip.id}`);

      const departureArrivalDetails = [
        {
          label: '',
          value: `${element.name} ${element.from.description} → ${element.to.description}`,
          translate: false
        }
      ];
      formatDetails(departureArrivalDetails);
      if (element.airReservationNumber) {
        result.push(`[予約番号] ${element.airReservationNumber}`);
      }
      if (element.airConfirmationNumber) {
        result.push(`[確認番号] ${element.airConfirmationNumber}`);
      }
      if (element.airSeatNumber) {
        result.push(`[座席番号] ${element.airSeatNumber}`);
      }
      const localTimeDetails = [{ label: '', value: '※ 表記は現地時刻です。', translate: true }];
      formatDetails(localTimeDetails);
      result.push('');

      formatTravelerTypes(orderItem.travelerShinkansenTypes());
      formatTravelerTypes(orderItem.travelerAirlineTypes);
      if (element.transportType === 'domestic_air') {
        result.push(`[席種] ${element.airSeatType || ''}`);
        result.push(
          `[券種] ${
            element.airTicketType === AIR_TICKET_TYPE_RAKUTEN_PACKAGE
              ? ''
              : element.airChangeableDescriptionWithFare || element.airTicketTypeStr || ''
          }`
        );
      }
      const isJrPackageFlag = orderItem.orderItemType === 10 || orderItem.orderItemCategory === 'jr_package';
      if (isJrPackageFlag && orderItem.jrAuthCode) {
        result.push(`[JR認証番号] ${orderItem.jrAuthCode}`);
      }
      if (isJrPackageFlag && orderItem.jrPickupCode) {
        result.push(`[JR引取番号] ${orderItem.jrPickupCode}`);
      }
      if (element.detailPath) result.push(`[詳細情報] ${element.detailPath}`);
    }
  });
  orderItem.eTickets.forEach(ticket => result.push(`e-ticket(${ticket.link})`));
  if (orderItem.price) result.push(`[金額] ${digits(orderItem.price.totalPrice)}円`);

  if (isAiTravel(serviceId) && isQrTicketingMethod(orderItem.ticketingMethod)) {
    pushQrNoticeLines(result, orderItem.ticketingMethod, orderItem.pickupStation);
  }

  result.push('');
  result.push(addBold('サービス'));
  result.push(addLink(`${location.origin}/trips/${trip.id}`));
  result.push('');

  if (isAiTravel(serviceId)) {
    result.push(addBold('その他'));
    result.push('当日の変更・キャンセルの際は、以下よりご確認ください。');
    result.push(addLink('https://aitravel.jp/knowledges/000001075'));
    result.push('');
  }

  return calendarType === 'google' ? result.join('\n') : result.join('<br>');
};

export interface ElementCalendarLink {
  label: string;
  link: string;
}

const formatElementBodyText = (
  element: Element,
  orderItem: OrderItem,
  trip: Trip,
  serviceId: number,
  calendarType: 'google' | 'outlook',
  nightIndex?: number
): string => {
  const result: string[] = [];
  const formatDetails = (details: Translatedline[]) =>
    details.forEach(({ label, value }) => result.push(`${label ? `${label} ` : ''}${value}`));

  const formatTravelerTypes = (travelerTypes: string[]) =>
    travelerTypes?.forEach((travelerType, i) =>
      result.push(`${travelerTypes.length > 1 ? `${i + 1}人目` : '出張者'}: ${travelerType}`)
    );

  if (element instanceof HotelElement) {
    result.push(addBold('旅程情報'));
    if (nightIndex !== undefined) {
      const totalNights = element.stayDays;
      const nightCheckin = element.checkinDate.clone().add(nightIndex, 'days');
      const nightCheckout = nightCheckin.clone().add(1, 'day');
      result.push(`${element.name}（${nightIndex + 1}泊目/全${totalNights}泊）`);
      result.push('');

      if (element.hotelReservationNumber) {
        result.push(`予約番号：${element.hotelReservationNumber}`);
      }

      const departureArrivalDetails = [
        {
          label: '[チェックイン]　',
          value: `${nightCheckin.format('YYYY-MM-DD')} ${nightIndex === 0 ? element.checkinTime || '' : ''}`,
          translate: false
        },
        {
          label: '[チェックアウト]',
          value: `${nightCheckout.format('YYYY-MM-DD')} ${
            nightIndex === totalNights - 1 ? element.checkoutTime || '' : ''
          }`,
          translate: false
        },
        { label: '', value: '※ 表記は現地時刻です。', translate: true }
      ];
      formatDetails(departureArrivalDetails);
    } else {
      result.push(element.name);
      result.push('');

      if (element.hotelReservationNumber) {
        result.push(`予約番号：${element.hotelReservationNumber}`);
      }

      const departureArrivalDetails = [
        {
          label: '[チェックイン]　',
          value: `${element.checkinDate.format('YYYY-MM-DD')} ${element.checkinTime || ''}`,
          translate: false
        },
        {
          label: '[チェックアウト]',
          value: `${element.checkoutDate.format('YYYY-MM-DD')} ${element.checkoutTime || ''}`,
          translate: false
        },
        { label: '', value: '※ 表記は現地時刻です。', translate: true }
      ];
      formatDetails(departureArrivalDetails);
    }
    result.push('');

    result.push(
      `[返金] ${
        element.refundable === true ? '可' : element.refundable === false ? '不可' : 'お問い合わせください'
      }`
    );

    const hotelInfoDetails = [{ label: '[プラン]', value: '', translate: false }]
      .concat(
        element.structuredHotelTypeDetail().flatMap((detail: Translatedline) => {
          const detailLine = {
            label: `　${detail.label}`,
            value: detail.value.replace(/^\n+/, '').trim(),
            translate: detail.translate,
            kind: detail.kind
          };
          if (detail.kind === 'plan_name') {
            return [detailLine, { label: '', value: HOTEL_PLAN_POINT_NOTICE, translate: true }];
          }
          return [detailLine];
        })
      )
      .concat([
        { label: '　住所', value: element.address || '', translate: false },
        { label: '　電話番号', value: element.tel || '', translate: false }
      ])
      .concat(
        element
          .structuredRoomTypeDetail()
          .map((detail: Translatedline) => formatRoomTypeDetailForCalendar(detail, calendarType))
      );
    formatDetails(hotelInfoDetails);

    if (element.cancelPolicy) {
      result.push('');
      result.push('[キャンセルポリシー]');

      if (element.cancelPolicy && typeof element.cancelPolicy === 'string') {
        const cancelPolicyLines = element.cancelPolicy.split('\n').filter((line: string) => line.trim());
        cancelPolicyLines.forEach((line: string) => result.push(line.trim()));
      }
    }
  } else if (element instanceof TransportElement) {
    result.push(`旅程番号: ${trip.id}`);

    const departureArrivalDetails = [
      {
        label: '',
        value: `${element.name} ${element.from.description} → ${element.to.description}`,
        translate: false
      }
    ];
    formatDetails(departureArrivalDetails);
    if (element.airReservationNumber) {
      result.push(`[予約番号] ${element.airReservationNumber}`);
    }
    if (element.airConfirmationNumber) {
      result.push(`[確認番号] ${element.airConfirmationNumber}`);
    }
    if (element.airSeatNumber) {
      result.push(`[座席番号] ${element.airSeatNumber}`);
    }
    const localTimeDetails = [{ label: '', value: '※ 表記は現地時刻です。', translate: true }];
    formatDetails(localTimeDetails);
    result.push('');

    formatTravelerTypes(orderItem.travelerShinkansenTypes());
    formatTravelerTypes(orderItem.travelerAirlineTypes);
    if (element.transportType === 'domestic_air') {
      result.push(`[席種] ${element.airSeatType || ''}`);
      result.push(
        `[券種] ${
          element.airTicketType === AIR_TICKET_TYPE_RAKUTEN_PACKAGE
            ? ''
            : element.airChangeableDescriptionWithFare || element.airTicketTypeStr || ''
        }`
      );
    }
    const isJrPackageFlag = orderItem.orderItemType === 10 || orderItem.orderItemCategory === 'jr_package';
    if (isJrPackageFlag && orderItem.jrAuthCode) {
      result.push(`[JR認証番号] ${orderItem.jrAuthCode}`);
    }
    if (isJrPackageFlag && orderItem.jrPickupCode) {
      result.push(`[JR引取番号] ${orderItem.jrPickupCode}`);
    }
    if (element.detailPath) result.push(`[詳細情報] ${element.detailPath}`);
  }

  orderItem.eTickets.forEach(ticket => result.push(`e-ticket(${ticket.link})`));
  if (orderItem.price) result.push(`[金額] ${digits(orderItem.price.totalPrice)}円`);

  if (isAiTravel(serviceId) && isQrTicketingMethod(orderItem.ticketingMethod)) {
    pushQrNoticeLines(result, orderItem.ticketingMethod, orderItem.pickupStation);
  }

  result.push('');
  result.push(addBold('サービス'));
  result.push(addLink(`${location.origin}/trips/${trip.id}`));
  result.push('');

  if (isAiTravel(serviceId)) {
    result.push(addBold('その他'));
    result.push('当日の変更・キャンセルの際は、以下よりご確認ください。');
    result.push(addLink('https://aitravel.jp/knowledges/000001075'));
    result.push('');
  }

  return calendarType === 'google' ? result.join('\n') : result.join('<br>');
};

const formatDateForElement = (
  element: Element,
  calendarType: 'google' | 'outlook',
  isEndDate: boolean,
  nightIndex?: number
): string => {
  if (element instanceof HotelElement) {
    const checkin =
      nightIndex !== undefined ? element.checkinDate.clone().add(nightIndex, 'days') : element.checkinDate;
    const date = isEndDate ? checkin.clone().add(1, 'day') : checkin;
    const formatString = calendarType === 'google' ? 'YYYYMMDD' : 'YYYY-MM-DD';
    return moment(date).format(formatString);
  }
  if (element instanceof TransportElement) {
    const timeObj = isEndDate ? element.to?.time : element.from?.time;
    if (timeObj) {
      return calendarType === 'outlook'
        ? moment(timeObj).format('YYYY-MM-DDTHH:mm:ss')
        : moment(timeObj).format('YYYYMMDDTHHmmSS');
    }
  }
  return '';
};

const getElementCalendarTitle = (element: Element, nightIndex?: number, totalNights?: number): string => {
  if (element instanceof HotelElement) {
    if (totalNights && totalNights > 1 && nightIndex !== undefined) {
      return `ホテル：${element.name}（${nightIndex + 1}泊目/全${totalNights}泊）`;
    }
    return `ホテル：${element.name}`;
  }
  if (element instanceof TransportElement) {
    const fromName = element.from?.name || '';
    const toName = element.to?.name || '';
    const departureTime = element.from?.time ? moment(element.from.time).format('HH:mm') : '';
    return `${element.title}：${element.name || ''}　${fromName}→${toName} ${departureTime}発`;
  }
  return '';
};

export const createCalendarLinksForElements = (
  orderItem: OrderItem,
  trip: Trip,
  userId: number,
  theme: Theme,
  serviceId: number,
  calendarType: 'google' | 'outlook'
): ElementCalendarLink[] => {
  const links: ElementCalendarLink[] = [];

  orderItem.elements.forEach(element => {
    if (element instanceof HotelElement) {
      const totalNights = element.stayDays;
      for (let nightIndex = 0; nightIndex < totalNights; nightIndex += 1) {
        const title = getElementCalendarTitle(element, nightIndex, totalNights);
        const body = formatElementBodyText(element, orderItem, trip, serviceId, calendarType, nightIndex);
        const start = formatDateForElement(element, calendarType, false, nightIndex);
        const end = formatDateForElement(element, calendarType, true, nightIndex);
        const link = createCalendarLink(title, body, start, end, calendarType, true);
        links.push({ label: title, link });
      }
    } else if (element instanceof TransportElement) {
      const title = getElementCalendarTitle(element);
      const body = formatElementBodyText(element, orderItem, trip, serviceId, calendarType);
      const start = formatDateForElement(element, calendarType, false);
      const end = formatDateForElement(element, calendarType, true);
      const link = createCalendarLink(title, body, start, end, calendarType, false);
      links.push({ label: title, link });
    }
  });

  return links;
};
